import React, { useEffect, useRef, useState } from 'react';
import DatePicker, { DateObject, DatePickerRef } from 'react-multi-date-picker';
import 'react-multi-date-picker/styles/colors/red.css';
import Icon from 'react-multi-date-picker/components/icon';
import { dayjs } from '@umanforge/common';
import Button from '@common_components/buttons/button.component';

interface DatePickerInputProp {
  label?: string;
  placeholder?: string;
  customButtons?: { label: string, dates: { start: dayjs.Dayjs, end: dayjs.Dayjs } | null }[];
  labelPosition?: 'top' | 'left';
  textSize?: string;
  minDate?: Date;
  maxDate?: Date;
  startDate?: Date | null;
  endDate?: Date | null;
  closeAfterSelect?: boolean;
  clearAfrerClose?: boolean;
  startMounthToSee?: Date
  onChange?: ( startDate: Date | null, endDate: Date | null ) => void;
  onClose?: ( startDate: Date, endDate: Date,  ) => void;
}

const DatePickerInput: React.FC<DatePickerInputProp> = ( {
  label,
  placeholder,
  customButtons,
  labelPosition = 'left',
  textSize = 'w-3/12 mr-4',
  minDate,
  maxDate,
  startDate,
  endDate,
  closeAfterSelect,
  clearAfrerClose,
  startMounthToSee,
  onChange,
  onClose,
} ) => {
  const localDayjs = dayjs.Ls[ dayjs.locale() ];
  const pickerRef = useRef();

  const [ start, setStart ] = useState( startDate );
  const [ end, setEnd ] = useState( endDate );
  const [ isOpen, setIsOpen ] = useState( false );

  useEffect( () => {
    if ( !isOpen ) {
      if ( onClose && start && end ) {
        onClose( start, end );
      }
      if ( clearAfrerClose ) {
        setStart( null );
        setEnd( null );
      }
    }
  }, [ isOpen ] );

  useEffect( () => {
    setStart( startDate ?? null );
    setEnd( endDate ?? null );
  }, [ startDate, endDate ] );

  const handleChange = ( dateObjects: ( DateObject[] | null ) ) => {
    if ( Array.isArray( dateObjects ) && dateObjects.length === 2 ) {
      const [ startD, endD ] = dateObjects;
      if ( startD && endD ) {
        setStart( startD.toDate() );
        setEnd( endD.toDate() );
        if ( onChange ) {
          onChange( startD.toDate(), endD.toDate() );
        }
        if ( closeAfterSelect ) {
          const ref = ( pickerRef.current as unknown as DatePickerRef | null );
          if ( ref ) {
            ref.closeCalendar();
          }
          setIsOpen( false );
        }
      }
    }
    if ( dateObjects == null && onChange ) {
      onChange( null, null );
    }
  };

  const convertToDateObject = ( date?: Date | null ) => {
    return date ? new DateObject( date ) : null;
  };

  return (
    <div className={`flex h-fit w-full ${
      labelPosition === 'left' ? '' : 'flex-col'
    }`}>
      {/* Label */}
      {label && (
        <label
          className={`text-gray-600 ${textSize} ${
            labelPosition === 'left' ? 'flex items-center' : ''
          }`}
        >
          {label}
        </label>
      )}

      {/* Input */}
      <div className={
        'flex h-full w-full m-0 items-center ' +
        'bg-white border-2 border-solid outline-0 rounded-md shadow ' +
        ( isOpen ? 'border-black' : 'border-white hover:border-black' )
      }>
        <DatePicker
          ref={ pickerRef }
          minDate={minDate}
          maxDate={maxDate}
          weekStartDayIndex={ localDayjs.weekStart }
          weekDays={ localDayjs.weekdaysShort }
          months={ localDayjs.months }
          format={ localDayjs.formats.L }
          zIndex={20}
          placeholder={ placeholder }
          value={[ convertToDateObject( start ), convertToDateObject( end ) ]}
          onChange={handleChange}
          range
          editable={ false }
          onOpen={ () => { setIsOpen( true ); } }
          onClose={ () => { setIsOpen( false ); } }
          showOtherDays
          currentDate={  new DateObject( startMounthToSee ? startMounthToSee : dayjs().startOf( 'month' ).toDate() ) }
          numberOfMonths={2}
        >
          {
            customButtons && customButtons.length > 0 && (
              <div className='flex flex-wrap h-fit w-[504px] gap-2 p-2 justify-center border-t border-gray-100 mx-2'>
                {
                  customButtons.map( button => {
                    const isAlreadySelected = button.dates != null && button.dates.start.isSame( dayjs( startDate ), 'day' ) && button.dates.end.isSame( dayjs( endDate ), 'day' );
                    return (
                      <div
                        key={ button.label + ( button.dates && ( button.dates.start.unix() + button.dates.end.unix() ) ) }
                        className='flex justify-center w-full'
                      >
                        <Button
                          label={ button.label }
                          width='w-[248px]'
                          height='h-4'
                          padding='px-3 py-3'
                          color='rmdp-input-custom-button'
                          textSize='text-[14px]'
                          textColor='text-gray-400'
                          disable={ isAlreadySelected }
                          onClick={() => {
                            if ( !isAlreadySelected ) {
                              let updatesDates: DateObject[] | null = null;
                              if ( button.dates ) {
                                updatesDates = [];
                                updatesDates.push( new DateObject( button.dates.start.toDate() ) );
                                updatesDates.push( new DateObject( button.dates.end.toDate() ) );
                              }
                              handleChange( updatesDates );
                              const ref = ( pickerRef.current as unknown as DatePickerRef | null );
                              if ( ref ) {
                                ref.closeCalendar();
                              }
                            }
                          } }
                        />
                      </div>
                    );
                  } )
                }
              </div>
            )
          }
        </DatePicker>
        <div
          className='h-full px-2 cursor-pointer'
          onClick={ () => {
            const ref = ( pickerRef.current as unknown as DatePickerRef | null );
            if ( ref ) {
              if ( isOpen ) {
                ref.closeCalendar();
              } else {
                ref.openCalendar();
              }
            }
          } }
        >
          <Icon className='h-full p-0.5'/>
        </div>
      </div>
    </div>
  );
};

export default DatePickerInput;
